import { Button, TextSpinner } from '@factorialco/deprecated-design-system'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useEditUnassignedShifts } from 'modules/shift_management/hooks/useEditUnassignedShifts'
import UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'

import Content from './Content'
import { formData, haveShiftDetailsChanged } from './utils'

interface Props {
  unassignedShift: UnassignedShift
  totalAssignedShifts: number
  onClose: () => void
  contractVersions: ContractVersions
  roles: ShiftManagementRolesRequirementsFragment[]
}

const EditUnassignedShiftModal = ({
  unassignedShift,
  totalAssignedShifts,
  onClose,
  contractVersions,
  roles,
}: Props) => {
  const initialAttributes = formData(unassignedShift)
  const unassignedShiftForm = useLazyRef(() => new UnassignedShiftForm(initialAttributes)).current
  const title = i18n.t('shift_management.coverage_section.unassigned_shift.edit_title')
  const description = i18n.t('shift_management.coverage_section.unassigned_shift.edit_description')

  const modifiedShiftDetails = haveShiftDetailsChanged(initialAttributes, unassignedShiftForm)

  const { onSubmit, submitting } = useEditUnassignedShifts({
    unassignedShiftForm,
    unassignedShift,
    modifiedShiftDetails,
    contractVersions,
  })

  const disabled = !modifiedShiftDetails || !unassignedShiftForm.isVisibilitySelected

  return (
    <Modal
      illustration="tasks_management"
      title={title}
      description={description}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button disabled={disabled} submit>
          {i18n.t('common.edit')}
        </Button>
      }
    >
      {submitting ? (
        <TextSpinner text={i18n.t('shift_management.actions.editing_shifts')} />
      ) : (
        <Content
          unassignedShiftForm={unassignedShiftForm}
          totalAssignedShifts={totalAssignedShifts}
          roles={roles}
        />
      )}
    </Modal>
  )
}

export default observer(EditUnassignedShiftModal)
